import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import hygeaLogo from '../../assets/hygea.png';
import { API_BASE } from '../../config/runtime';

const statusStyles = {
  Live: 'bg-[#d1fae5] text-[#065f46]',
  Scheduled: 'bg-[#fdf2f8] text-[#94154c]',
  Ended: 'bg-[#f3f4f7] text-[#5d5f6c]'
};

const formatPeriod = (startDate, endDate) => {
  if (startDate && endDate) return `${startDate} - ${endDate}`;
  if (startDate) return startDate;
  if (endDate) return endDate;
  return 'TBD';
};

// ── Export helpers ─────────────────────────────────────────────────────────

const buildExportRows = (promos, branchNameFn) =>
  promos.map((promo) => ({
    Name: promo.name,
    Code: promo.promo_code ?? promo.code ?? '',
    Status: promo.status ?? '',
    'Start Date': promo.startDate ?? '',
    'End Date': promo.endDate ?? '',
    'Discount / Offer': promo.discount ?? '',
    Branches:
      promo.branches && promo.branches.length > 0
        ? promo.branches.map((id) => branchNameFn(id)).join(', ')
        : 'All branches',
    Description: promo.description ?? ''
  }));

const exportAsXLSX = async (rows, filename) => {
  let XLSX = window.XLSX;
  if (!XLSX) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    XLSX = window.XLSX;
  }
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Promotions');
  XLSX.writeFile(wb, filename);
};

const exportAsPDF = async (rows, filename) => {
  let jsPDF = window.jspdf?.jsPDF;
  if (!jsPDF) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    jsPDF = window.jspdf?.jsPDF;
  }
  if (!window.jspdfAutoTable) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    window.jspdfAutoTable = true;
  }

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(16);
  doc.setTextColor('#6f4a42');
  doc.text('Hygea Wellness — Promotions Export', 40, 40);
  doc.setFontSize(9);
  doc.setTextColor('#888');
  doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 56);

  const columns = Object.keys(rows[0] || {});
  const body = rows.map((row) => columns.map((col) => row[col] ?? ''));

  doc.autoTable({
    head: [columns],
    body,
    startY: 70,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [179, 123, 101], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [253, 248, 244] },
    margin: { left: 40, right: 40 }
  });

  doc.save(filename);
};

// ── Export Format Modal ────────────────────────────────────────────────────
const ExportModal = ({ onClose, onExport }) => {
  const [format, setFormat] = useState('xlsx');
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 px-4 pb-4 sm:pb-0">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 sm:p-7 shadow-[0_25px_80px_rgba(0,0,0,0.3)]">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#b07b60]">Export Promotions</p>
        <h3 className="mt-1 text-xl font-bold text-gray-800">Choose format</h3>
        <p className="mt-1 text-sm text-gray-500">Select a file format to download the current filtered list.</p>

        <div className="mt-5 grid grid-cols-2 gap-3">
          {['xlsx', 'pdf'].map((fmt) => (
            <button
              key={fmt}
              type="button"
              onClick={() => setFormat(fmt)}
              className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-4 transition ${
                format === fmt
                  ? 'border-[#b37b65] bg-[#fdf6f2]'
                  : 'border-[#e0d3c7] bg-white hover:border-[#c9a08a]'
              }`}
            >
              {fmt === 'xlsx' ? (
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17v-2m3 2v-4m3 4v-6M4 20h16a1 1 0 001-1V7l-5-5H4a1 1 0 00-1 1v16a1 1 0 001 1z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              )}
              <span className="text-sm font-bold uppercase tracking-wider text-gray-700">{fmt.toUpperCase()}</span>
              <span className="text-[10px] text-gray-400">
                {fmt === 'xlsx' ? 'Excel spreadsheet' : 'PDF document'}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-[#d7c2b7] px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#8d776c] hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onExport(format)}
            className="rounded-2xl bg-gradient-to-r from-[#b37b65] to-[#996758] px-6 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-sm transition hover:-translate-y-0.5"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const PromoManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPromos, setIsLoadingPromos] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branches, setBranches] = useState([]);
  const [promoList, setPromoList] = useState([]);
  const [form, setForm] = useState({
    name: '',
    code: '',
    startDate: '',
    endDate: '',
    discount: '',
    description: '',
    branches: []
  });
  const [editingPromo, setEditingPromo] = useState(null);
  const [deletingPromo, setDeletingPromo] = useState(null);

  const getBranchName = (branchId) => {
    const id = Number(branchId);
    const match = branches.find(
      (branch) => Number(branch.branch_id ?? branch.id ?? branch.branchId) === id
    );
    return match?.branch_name ?? match?.name ?? 'Branch';
  };

  const promoStats = useMemo(() => {
    const counts = { Live: 0, Scheduled: 0, Ended: 0 };
    promoList.forEach((promo) => {
      if (counts[promo.status] !== undefined) counts[promo.status] += 1;
    });
    const total = promoList.length;
    const redemptionRate = total ? `${Math.round((counts.Live / total) * 100)}%` : '—';
    return [
      { label: 'Active promotions',  value: counts.Live,      accent: 'text-[#1d6f47]' },
      { label: 'Scheduled launches', value: counts.Scheduled, accent: 'text-[#b37b65]' },
      { label: 'Redemption rate',    value: redemptionRate,   accent: 'text-[#6f58a0]' }
    ];
  }, [promoList]);

  const filteredPromos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const fromBoundary = dateFrom ? new Date(dateFrom) : null;
    const toBoundary   = dateTo   ? new Date(dateTo)   : null;
    return promoList.filter((promo) => {
      if (filterStatus !== 'all' && promo.status !== filterStatus) return false;
      if (fromBoundary && promo.endDate && new Date(promo.endDate) < fromBoundary) return false;
      if (toBoundary && promo.startDate && new Date(promo.startDate) > toBoundary) return false;
      if (!term) return true;
      const haystack = `${promo.name} ${promo.promo_code ?? promo.code} ${promo.description}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [promoList, searchTerm, filterStatus, dateFrom, dateTo]);

  const loadBranches = async () => {
    try {
      const response = await fetch(`${API_BASE}/get_branches.php`);
      const data = await response.json();
      if (data.success) {
        setBranches(data.branches || []);
      } else {
        setStatusMessage({ type: 'error', text: data.message || 'Unable to load branches' });
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Unable to load branches: ' + error.message });
    }
  };

  const loadPromos = async () => {
    setIsLoadingPromos(true);
    try {
      const response = await fetch(`${API_BASE}/get_promos.php`);
      const data = await response.json();
      if (data.success) {
        setPromoList(data.promos || []);
        setStatusMessage(null);
      } else {
        setStatusMessage({ type: 'error', text: data.message || 'Unable to load promotions' });
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Unable to load promotions: ' + error.message });
    } finally {
      setIsLoadingPromos(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', code: '', startDate: '', endDate: '', discount: '', description: '', branches: [] });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchToggle = (branchId) => {
    const id = Number(branchId);
    if (!id) return;
    setForm((prev) => {
      const exists = prev.branches?.includes(id);
      const next = exists
        ? prev.branches.filter((b) => b !== id)
        : [...(prev.branches || []), id];
      return { ...prev, branches: next };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(null);
    setIsSaving(true);

    const payload = {
      promo_id:    editingPromo?.id ?? null,
      name:        form.name.trim(),
      promo_code:  form.code.trim(),
      start_date:  form.startDate || null,
      end_date:    form.endDate || null,
      discount:    form.discount.trim(),
      description: form.description.trim(),
      branches:    form.branches || [],
      status:      editingPromo?.status ?? 'Scheduled'
    };

    if (!payload.name || !payload.promo_code || !payload.start_date) {
      setStatusMessage({ type: 'error', text: 'Name, code, and start date are required' });
      setIsSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/save_promo.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to save promotion');
      const savedPromo = data.promo;
      setPromoList((prev) => {
        if (editingPromo) return prev.map((p) => (p.id === savedPromo.id ? savedPromo : p));
        return [savedPromo, ...prev];
      });
      setStatusMessage({ type: 'success', text: data.message || (editingPromo ? 'Promotion updated' : 'Promotion created') });
      setIsModalOpen(false);
      setEditingPromo(null);
      resetForm();
    } catch (error) {
      setStatusMessage({ type: 'error', text: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  const openModalForAdd = () => {
    resetForm();
    setEditingPromo(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (promo) => {
    setForm({
      name:        promo.name,
      code:        promo.promo_code ?? promo.code,
      startDate:   promo.startDate || '',
      endDate:     promo.endDate || '',
      discount:    promo.discount || '',
      description: promo.description || '',
      branches:    promo.branches || []
    });
    setEditingPromo(promo);
    setIsModalOpen(true);
  };

  const requestDeletePromo = (promo) => setDeletingPromo(promo);

  const handleDeletePromo = async () => {
    if (!deletingPromo) return;
    setIsDeleting(true);
    setStatusMessage(null);
    try {
      const response = await fetch(`${API_BASE}/delete_promo.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promo_id: deletingPromo.id })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to delete promotion');
      setPromoList((prev) => prev.filter((p) => p.id !== deletingPromo.id));
      setStatusMessage({ type: 'success', text: 'Promotion deleted' });
      setDeletingPromo(null);
    } catch (error) {
      setStatusMessage({ type: 'error', text: error.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async (format) => {
    setShowExportModal(false);
    if (filteredPromos.length === 0) {
      setStatusMessage({ type: 'info', text: 'No promotions to export.' });
      return;
    }
    setIsExporting(true);
    setStatusMessage(null);
    try {
      const rows = buildExportRows(filteredPromos, getBranchName);
      const timestamp = new Date().toISOString().slice(0, 10);
      if (format === 'xlsx') {
        await exportAsXLSX(rows, `hygea-promotions-${timestamp}.xlsx`);
        setStatusMessage({ type: 'success', text: `Exported ${rows.length} promotion(s) as XLSX.` });
      } else {
        await exportAsPDF(rows, `hygea-promotions-${timestamp}.pdf`);
        setStatusMessage({ type: 'success', text: `Exported ${rows.length} promotion(s) as PDF.` });
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: 'Export failed: ' + err.message });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    loadBranches();
    loadPromos();
  }, []);

  return (
    <div className="flex min-h-screen bg-[#f7f4f1]">
      <AdminSidebar />

      {/* Main content — shrinks beside sidebar on md+ */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* ── Top bar ── */}
        <div className="bg-white shadow-sm px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Promo Management</h1>
          <img src={hygeaLogo} alt="Hygea Wellness Spa" className="h-9 sm:h-12" />
        </div>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="space-y-5 sm:space-y-6">

            {/* ── Overview card ── */}
            <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-5 sm:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b07b60]">Promo Overview</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-800">Keep campaigns in sync</h2>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                Track active and upcoming promotions, preview codes, and communicate launch timing to the rest of the team.
              </p>
              {/* 1 col → 3 col */}
              <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {promoStats.filter((stat) => stat.label !== 'Redemption rate').map((stat) => (
                  <div key={stat.label} className="rounded-2xl border border-[#e3d7ce] bg-[#faf6f3] p-4 shadow-sm">
                    <p className="text-sm font-semibold text-gray-500">{stat.label}</p>
                    <p className={`mt-1 text-2xl font-bold ${stat.accent}`}>{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Promotion table card ── */}
            <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-5 sm:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">

              {/* Card header + controls */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b07b60]">Promotion table</p>
                  <h2 className="mt-1 text-xl sm:text-2xl font-bold text-gray-800">Active &amp; Scheduled Offers</h2>
                </div>

                {/* Filter / action toolbar — wraps naturally */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Search */}
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full xs:w-[130px] rounded-2xl border border-[#d7c2b7] bg-[#fffdfa] px-4 py-2 text-xs text-[#4f4a45] transition-all duration-200 ease-in-out focus:w-full xs:focus:w-[220px] focus:border-[#b37b65] focus:outline-none"
                  />

                  {/* Status filter */}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="rounded-2xl border border-[#d7c2b7] bg-[#fffdfa] px-3 py-2 text-xs text-[#4f4a45] focus:border-[#b37b65] focus:outline-none"
                  >
                    <option value="all">All</option>
                    <option value="Live">Live</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="Ended">Ended</option>
                  </select>

                  {/* Date range — stacks to 2-col on xs, inline on sm+ */}
                  <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#5c4a42]">
                    <span className="hidden sm:inline">Date</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-9 rounded-2xl border border-[#d7c2b7] bg-[#fffdfa] px-2 text-xs text-[#4f4a45] focus:border-[#b37b65] focus:outline-none"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-9 rounded-2xl border border-[#d7c2b7] bg-[#fffdfa] px-2 text-xs text-[#4f4a45] focus:border-[#b37b65] focus:outline-none"
                    />
                  </div>

                  {/* Export */}
                  <button
                    type="button"
                    onClick={() => setShowExportModal(true)}
                    disabled={isExporting}
                    className="rounded-2xl border border-[#d7c2b7] bg-[#fdf2e9] px-4 py-2 text-xs font-semibold text-[#a05d3b] shadow-sm transition hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {isExporting ? 'Exporting…' : 'Export'}
                  </button>

                  {/* Add */}
                  <button
                    type="button"
                    onClick={openModalForAdd}
                    className="rounded-2xl border border-[#d7c2b7] bg-gradient-to-r from-[#fdf2e9] to-[#fff6ef] px-4 py-2 text-xs sm:text-sm font-semibold text-[#a05d3b] shadow-sm transition hover:-translate-y-0.5 whitespace-nowrap"
                  >
                    Add Promotion
                  </button>
                </div>
              </div>

              {/* Status message */}
              {statusMessage && (
                <div className={`mt-4 rounded-2xl border px-4 py-2 text-sm ${
                  statusMessage.type === 'error'
                    ? 'border-[#f1a9a9] bg-[#fff4f4] text-[#6a1f1f]'
                    : statusMessage.type === 'success'
                      ? 'border-[#cfe3ff] bg-[#eff6ff] text-[#1f2d37]'
                      : 'border-[#ececec] bg-[#fafafa] text-[#4f4a45]'
                }`}>
                  {statusMessage.text}
                </div>
              )}

              {/* Table — scrolls horizontally on small screens */}
              <div className="mt-5 sm:mt-6 -mx-5 sm:-mx-8 px-5 sm:px-8 overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm text-[#4f4a45]">
                  <thead>
                    <tr className="text-xs font-semibold uppercase tracking-[0.24em] text-[#83685a]">
                      <th className="pb-3 pt-1 pr-4">Name</th>
                      <th className="pb-3 pt-1 pr-4 whitespace-nowrap">Period</th>
                      <th className="pb-3 pt-1 pr-4">Code</th>
                      <th className="pb-3 pt-1 pr-4">Status</th>
                      <th className="pb-3 pt-1 pr-4">Branches</th>
                      {/* Hide "Details" column on xs/sm to save space */}
                      <th className="pb-3 pt-1 pr-4 hidden md:table-cell">Details</th>
                      <th className="pb-3 pt-1 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingPromos && (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-sm text-[#6f4a42]">Loading promotions...</td>
                      </tr>
                    )}
                    {!isLoadingPromos && filteredPromos.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-sm text-[#6f4a42]">No promotions matched your filters.</td>
                      </tr>
                    )}
                    {filteredPromos.map((promo) => (
                      <tr key={promo.id ?? promo.code} className="border-t border-[#f0e5da]">
                        <td className="py-3 sm:py-4 pr-4 font-semibold text-gray-800 whitespace-nowrap">{promo.name}</td>
                        <td className="py-3 sm:py-4 pr-4 text-[#6f4a42] whitespace-nowrap text-xs sm:text-sm">{formatPeriod(promo.startDate, promo.endDate)}</td>
                        <td className="py-3 sm:py-4 pr-4 font-mono text-[#a05d3b] whitespace-nowrap">{promo.promo_code ?? promo.code}</td>
                        <td className="py-3 sm:py-4 pr-4">
                          <span className={`inline-flex rounded-full px-2 sm:px-3 py-1 text-xs font-semibold whitespace-nowrap ${statusStyles[promo.status]}`}>
                            {promo.status}
                          </span>
                        </td>
                        <td className="py-3 sm:py-4 pr-4 text-xs sm:text-sm leading-6 text-[#51483f]">
                          {promo.branches && promo.branches.length > 0
                            ? promo.branches.map((id) => getBranchName(id)).join(', ')
                            : 'All branches'}
                        </td>
                        {/* Hidden on xs/sm */}
                        <td className="py-3 sm:py-4 pr-4 text-xs sm:text-sm leading-6 text-[#51483f] hidden md:table-cell max-w-[200px] truncate">{promo.description}</td>
                        <td className="py-3 sm:py-4 text-right">
                          <div className="inline-flex gap-1 sm:gap-2 text-xs font-semibold uppercase tracking-[0.3em]">
                            <button
                              type="button"
                              onClick={() => openModalForEdit(promo)}
                              className="rounded-2xl border border-[#b0917f] px-2 sm:px-3 py-1 text-[#6f4a42] transition hover:border-[#a05d3b] hover:text-[#a05d3b]"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDeletePromo(promo)}
                              className="rounded-2xl border border-transparent bg-[#fde8df] px-2 sm:px-3 py-1 text-[#b94f35] transition hover:bg-[#fbd2c1]"
                            >
                              Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ── Add / Edit Modal ── */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4 py-0 sm:py-6">
            <div className="w-full sm:max-w-2xl max-h-[95dvh] overflow-y-auto space-y-5 rounded-t-3xl sm:rounded-3xl bg-white p-5 sm:p-8 shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
              {/* Modal header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#b07b60]">
                    {editingPromo ? 'Update Promotion' : 'New Promotion'}
                  </p>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {editingPromo ? 'Edit campaign' : 'Create campaign'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); setEditingPromo(null); }}
                  className="rounded-full border border-[#dfd1c5] p-2 text-gray-500 transition hover:bg-[#f4efeb]"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form className="grid gap-4" onSubmit={handleFormSubmit}>
                {/* Name + Code — stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                    Promotion name
                    <input name="name" value={form.name} onChange={handleFormChange} required placeholder="Wellness Week"
                      className="w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                  </label>
                  <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                    Code
                    <input name="code" value={form.code} onChange={handleFormChange} required placeholder="WELLNESS20"
                      className="w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                  </label>
                </div>

                {/* Dates — stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                    Start date
                    <input type="date" name="startDate" value={form.startDate} onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                  </label>
                  <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                    End date
                    <input type="date" name="endDate" value={form.endDate} onChange={handleFormChange}
                      className="w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                  </label>
                </div>

                {/* Branches */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">Available branches</p>
                  {branches.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] p-4 text-sm text-[#4f4a45]">
                      {branches.map((branch) => {
                        const label = branch.branch_name ?? branch.name ?? 'Branch';
                        const id = branch.branch_id ?? branch.id ?? branch.branchId ?? label;
                        const isSelected = form.branches?.includes(Number(id));
                        return (
                          <label key={id} className="flex items-center gap-2 leading-tight">
                            <input
                              type="checkbox"
                              checked={Boolean(isSelected)}
                              onChange={() => handleBranchToggle(id)}
                              className="h-4 w-4 rounded border-[#b0917f] text-[#a05d3b] focus:ring-[#a05d3b]"
                            />
                            <span className="text-sm font-semibold">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-[#6f4a42]">Loading branch availability…</p>
                  )}
                </div>

                {/* Discount */}
                <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                  Discount / offer
                  <input name="discount" value={form.discount} onChange={handleFormChange} placeholder="30% off, 2-for-1, etc."
                    className="w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                </label>

                {/* Description */}
                <label className="space-y-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#83685a]">
                  Description
                  <textarea name="description" value={form.description} onChange={handleFormChange} rows={3}
                    placeholder="Describe the focus of this promotion..."
                    className="min-h-[90px] w-full rounded-2xl border border-[#e0d3c7] bg-[#fdfaf7] px-4 py-2 text-sm text-[#4f4a45] focus:border-[#b37b65] focus:outline-none" />
                </label>

                {/* Footer buttons */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { setIsModalOpen(false); setEditingPromo(null); }}
                    className="rounded-2xl border border-[#d7c2b7] px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#8d776c]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="rounded-2xl bg-gradient-to-r from-[#b37b65] to-[#996758] px-6 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-sm transition hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving…' : 'Save Promotion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Delete Confirm Modal ── */}
        {deletingPromo && (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4">
            <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-[#e2dace] bg-white p-5 sm:p-6 shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#b07b60]">Confirm Delete</p>
              <h3 className="mt-1 text-xl font-bold text-gray-800">Delete {deletingPromo.name}?</h3>
              <p className="mt-2 text-sm text-[#4f4a45]">
                This action cannot be undone. All settings and branch availability tied to this promotion will be lost.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeletingPromo(null)}
                  className="rounded-2xl border border-[#d7c2b7] px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#8d776c]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeletePromo}
                  disabled={isDeleting}
                  className="rounded-2xl bg-[#b94f35] px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-sm transition hover:bg-[#a12f2a] disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Export Format Modal ── */}
        {showExportModal && (
          <ExportModal
            onClose={() => setShowExportModal(false)}
            onExport={handleExport}
          />
        )}
      </div>
    </div>
  );
};

export default PromoManagement;
